import { FileRepository } from "@/components/features";
import { AppModal, Button, LoadingSpinner } from "@/components/ui";
import { FormLoadingOverlay } from "@/components/ui/Form/FormLoadingOverlay";
import { FormCancelButton } from "@/components/ui/FormFields/FormCancelButton/FormCancelButton";
import type { Matter, MatterFile } from "@/lib/types";
import { Suspense, useCallback, useEffect, useState } from "react";
import toast from "react-hot-toast";

interface SelectDocumentModalProps {
  open: boolean;
  setOpen: (open: boolean) => void;
  matter: Matter;
  onDocumentSelected: (files: MatterFile[]) => Promise<void> | void;
  enabledFileTypes?: string[] | undefined;
  multiple?: boolean;
  maxSelection?: number;
  onSave?: (files: MatterFile[]) => Promise<void>;
  // Optional set of file IDs to exclude from selection (e.g., files already in a share folder)
  excludedFileIds?: Set<string>;
  title?: string;
  description?: string;
  confirmActionLabel?: string;
}

export const SelectDocumentModal = ({
  open,
  setOpen,
  matter,
  onDocumentSelected,
  enabledFileTypes,
  multiple = false,
  maxSelection,
  onSave,
  excludedFileIds,
  title = "Select document",
  description = "Choose a document for the signature request",
  confirmActionLabel = "Save",
}: SelectDocumentModalProps) => {
  const [selectedFiles, setSelectedFiles] = useState<MatterFile[]>([]);
  const [isSaving, setIsSaving] = useState(false);
  const [isConfirmingSelection, setIsConfirmingSelection] = useState(false);

  const handleExternalSelectionChange = useCallback(
    (fileIds: string[], checked: boolean, replaceSelection = false) => {
      if (replaceSelection) {
        const nextFile = fileIds
          .map((id) => matter.files.find((file) => file.id === id))
          .find(Boolean);
        setSelectedFiles(nextFile ? [nextFile] : []);
        return;
      }

      if (!multiple) {
        if (checked) {
          const nextFile = fileIds
            .map((id) => matter.files.find((file) => file.id === id))
            .find(Boolean);
          if (!nextFile) return;
          setSelectedFiles([nextFile]);
        } else {
          setSelectedFiles((prev) =>
            prev.some((file) => fileIds.includes(file.id)) ? [] : prev,
          );
        }
        return;
      }

      setSelectedFiles((prev) => {
        const prevMap = new Map(prev.map((file) => [file.id, file]));
        if (!checked) {
          return prev.filter((file) => !fileIds.includes(file.id));
        }

        const next = [...prev];
        const maxAllowed = maxSelection ? maxSelection - next.length : Infinity;
        if (maxAllowed <= 0) return prev;

        let added = 0;
        for (const fileId of fileIds) {
          if (prevMap.has(fileId)) continue;
          const file = matter.files.find((item) => item.id === fileId);
          if (!file) continue;
          next.push(file);
          added += 1;
          if (added >= maxAllowed) break;
        }
        return next;
      });
    },
    [matter.files, maxSelection, multiple],
  );

  const handleConfirmSelection = async () => {
    if (selectedFiles.length === 0 || isConfirmingSelection) return;
    setIsConfirmingSelection(true);
    try {
      await onDocumentSelected(selectedFiles);
      setSelectedFiles([]);
      setOpen(false);
    } catch {
      toast.error("Unable to add document. Please try again.");
    } finally {
      setIsConfirmingSelection(false);
    }
  };

  const handleSave = async (closeAfter: boolean) => {
    if (!onSave || selectedFiles.length === 0) {
      toast.error("No files selected");
      return;
    }

    setIsSaving(true);
    try {
      await onSave(selectedFiles);
      setSelectedFiles([]);
      if (closeAfter) {
        setOpen(false);
      }
    } catch (err) {
      console.error("Error saving files:", err);
      toast.error("Error saving files");
    } finally {
      setIsSaving(false);
    }
  };

  useEffect(() => {
    if (!open) {
      setSelectedFiles([]);
    }
  }, [open]);

  const saveLabel = multiple
    ? `${confirmActionLabel} (${selectedFiles.length})`
    : confirmActionLabel;
  const saveAndCloseLabel = multiple
    ? `${confirmActionLabel} and close (${selectedFiles.length})`
    : `${confirmActionLabel} and close`;

  const content = (
    <>
      {(isSaving || isConfirmingSelection) && <FormLoadingOverlay />}
      <div className="relative flex h-full min-h-0 flex-1 flex-col overflow-hidden">
        <div className="min-h-0 flex-1 overflow-hidden">
          <Suspense
            fallback={
              <div className="flex h-full items-center justify-center">
                <LoadingSpinner />
              </div>
            }
          >
            <FileRepository
              matter={matter}
              useExternalSelection
              onExternalSelectionChange={handleExternalSelectionChange}
              selectedFiles={selectedFiles}
              enabledFileTypes={enabledFileTypes}
              showMergeDocuments={false}
              showShareFolders={false}
              maxSelection={maxSelection}
              excludedFileIds={excludedFileIds}
              showSelectionActions={false}
              enableTreeDrag={false}
            />
          </Suspense>
        </div>
      </div>
    </>
  );

  return (
    <AppModal
      open={open}
      onOpenChange={setOpen}
      title={title}
      description={description}
      size="4xl"
      overlayClassName="z-[102] bg-black/45 backdrop-blur-md"
      className="z-[103] h-[min(90vh,860px)]"
      bodyClassName="min-h-0"
      loading={isSaving || isConfirmingSelection}
      isDirty={selectedFiles.length > 0}
      bodyContained
      footer={
        <div className="flex w-full items-center justify-end gap-2">
          <FormCancelButton
            setOpen={setOpen}
            isDirty={selectedFiles.length > 0}
          />
          {onSave ? (
            <>
              <Button
                type="button"
                onClick={() => handleSave(false)}
                disabled={selectedFiles.length === 0 || isSaving}
                variant="outline"
              >
                {saveLabel}
              </Button>
              <Button
                type="button"
                onClick={() => handleSave(true)}
                disabled={selectedFiles.length === 0 || isSaving}
              >
                {saveAndCloseLabel}
              </Button>
            </>
          ) : (
            <Button
              type="button"
              onClick={handleConfirmSelection}
              disabled={selectedFiles.length === 0 || isConfirmingSelection}
            >
              {isConfirmingSelection && (
                <LoadingSpinner className="mr-2 h-4 w-4" />
              )}
              {saveLabel}
            </Button>
          )}
        </div>
      }
    >
      {content}
    </AppModal>
  );
};
